import { toast } from 'sonner'

const MAX_FILE_SIZE = 40 * 1024 * 1024

// Supported file extensions based on backend capabilities
const SUPPORTED_EXTENSIONS = [
  // PDF
  '.pdf',
  // Text files
  '.txt',
  '.md',
  '.csv',
  '.json',
  '.xml',
  '.html',
  '.log',
  '.rtf',
  '.yaml',
  '.yml',
  '.ini',
  '.conf',
  // Code files
  '.py',
  '.js',
  '.ts',
  '.jsx',
  '.tsx',
  '.java',
  '.cpp',
  '.c',
  '.h',
  '.hpp',
  '.cs',
  '.go',
  '.rs',
  '.rb',
  '.php',
  '.swift',
  '.kt',
  '.scala',
  '.sh',
  '.bash',
  '.zsh',
  '.ps1',
  '.bat',
  '.cmd',
]

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

export const validateFileType = (file: File): boolean => {
  const fileName = file.name.toLowerCase()
  const extension = fileName.substring(fileName.lastIndexOf('.'))

  if (!SUPPORTED_EXTENSIONS.includes(extension)) {
    toast.error(
      `File "${file.name}" is not supported. Supported formats: PDF and text files (${SUPPORTED_EXTENSIONS.slice(0, 5).join(', ')}, and more).`
    )
    return false
  }
  return true
}

export const validateFileSize = (file: File): boolean => {
  if (file.size > MAX_FILE_SIZE) {
    toast.error(`File "${file.name}" exceeds the 40MB size limit.`)
    return false
  }
  return true
}

export const validateTotalFileSize = (files: File[]): boolean => {
  const totalSize = files.reduce((sum, file) => sum + file.size, 0)
  if (totalSize > MAX_FILE_SIZE) {
    toast.error(
      `Total file size (${formatFileSize(totalSize)}) exceeds the 40MB limit. Please select files with a combined size of 40MB or less.`
    )
    return false
  }
  return true
}

// MIME types and extensions for react-dropzone accept prop
export const ACCEPTED_FILE_TYPES = {
  'application/pdf': ['.pdf'],
  'text/plain': ['.txt'],
  'text/markdown': ['.md'],
  'text/csv': ['.csv'],
  'application/json': ['.json'],
  'application/xml': ['.xml'],
  'text/xml': ['.xml'],
  'text/html': ['.html'],
  'text/rtf': ['.rtf'],
  'text/yaml': ['.yaml', '.yml'],
  'text/x-ini': ['.ini'],
  'text/x-config': ['.conf'],
  // Code files
  'text/x-python': ['.py'],
  'application/javascript': ['.js'],
  'application/typescript': ['.ts'],
  'text/javascript': ['.js'],
  'text/typescript': ['.ts'],
  'text/jsx': ['.jsx'],
  'text/tsx': ['.tsx'],
  'text/x-java': ['.java'],
  'text/x-c++': ['.cpp', '.hpp'],
  'text/x-c': ['.c', '.h'],
  'text/x-csharp': ['.cs'],
  'text/x-go': ['.go'],
  'text/x-rust': ['.rs'],
  'text/x-ruby': ['.rb'],
  'text/x-php': ['.php'],
  'text/x-swift': ['.swift'],
  'text/x-kotlin': ['.kt'],
  'text/x-scala': ['.scala'],
  'text/x-shellscript': ['.sh', '.bash', '.zsh'],
  'application/x-powershell': ['.ps1'],
  'application/x-msdos-program': ['.bat', '.cmd'],
}

// Get all accepted extensions as a flat array for display
export const getSupportedExtensions = (): string[] => {
  return SUPPORTED_EXTENSIONS
}
